import React, { useEffect, useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { Modal, Button, Badge, Nav, Tab } from "react-bootstrap"
import { toast } from "react-toastify"
import { confirmAlert } from "react-confirm-alert"
import moment from "moment"
import API from "../../common/API"

interface OrgRecord {
  id: string
  name: string
  identifier?: string
  nextBillingDate?: string
  subscriptionName?: string
  modules?: string
  creatorId?: string
  creatorName?: string
  creatorEmail?: string
  created?: string
}

interface StaffRecord {
  staffId: string
  userId: string
  fullName: string
  email: string
  roleId?: string
  roleName?: string
  joined?: string
}

interface RoleRecord {
  id: string
  name: string
}

interface UserSearchResult {
  id: string
  firstName: string
  lastName: string
  email: string
}

interface ActivityRecord {
  id: string
  userName?: string
  section?: string
  action?: string
  content?: string
  date?: string
  ipAddress?: string
}

const PAGE_SIZE = 10
const LOG_SIZE = 20

const AdminOrganizationsComponent = (_props: any) => {
  const [t] = useTranslation("global")

  // List state
  const [records, setRecords] = useState<OrgRecord[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(1)

  // Profile modal state
  const [selectedOrg, setSelectedOrg] = useState<OrgRecord | null>(null)
  const [showProfile, setShowProfile] = useState(false)
  const [activeTab, setActiveTab] = useState("info")

  // Staff tab
  const [staff, setStaff] = useState<StaffRecord[]>([])
  const [staffLoading, setStaffLoading] = useState(false)
  const [roles, setRoles] = useState<RoleRecord[]>([])
  const [userSearch, setUserSearch] = useState("")
  const [userResults, setUserResults] = useState<UserSearchResult[]>([])
  const [userSearchLoading, setUserSearchLoading] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState("")
  const [selectedUserLabel, setSelectedUserLabel] = useState("")
  const [selectedRoleId, setSelectedRoleId] = useState("")
  const [assigning, setAssigning] = useState(false)

  // Activity tab
  const [activity, setActivity] = useState<ActivityRecord[]>([])
  const [activityLoading, setActivityLoading] = useState(false)
  const [activityPage, setActivityPage] = useState(0)
  const [activityTotalPages, setActivityTotalPages] = useState(1)

  // Add/Edit modal
  const [showAddEdit, setShowAddEdit] = useState(false)
  const [editOrg, setEditOrg] = useState<Partial<OrgRecord>>({})
  const [saving, setSaving] = useState(false)

  const fetchRecords = useCallback(async (p: number, s: string) => {
    setIsLoading(true)
    try {
      const res = await API.getAction(
        `organization/AdminOrganizations?page=${p}&quantity=${PAGE_SIZE}&search=${encodeURIComponent(s)}`
      )
      if (res.data.status === "ok") {
        setRecords(res.data.response)
        setTotalPages(res.data.quantityOfPages || 1)
      }
    } catch {
      toast.error("Error cargando compañías")
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => { fetchRecords(page, search) }, [page])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(0)
    fetchRecords(0, search)
  }

  // ── Profile ──────────────────────────────────────────────────────────────────
  const openProfile = async (org: OrgRecord) => {
    setSelectedOrg(org)
    setActiveTab("info")
    setShowProfile(true)
    setActivity([])
    setActivityPage(0)
    setStaff([])
    setUserSearch("")
    setUserResults([])
    setSelectedUserId("")
    setSelectedUserLabel("")
    setSelectedRoleId("")
    await Promise.all([fetchStaff(org.id), fetchRoles(org.id)])
  }

  const handleTabSelect = async (key: string | null) => {
    if (!key || !selectedOrg) return
    setActiveTab(key)
    if (key === "activity" && activity.length === 0) {
      await fetchActivity(selectedOrg.id, 0)
    }
  }

  // ── Staff ─────────────────────────────────────────────────────────────────────
  const fetchStaff = async (orgId: string) => {
    setStaffLoading(true)
    try {
      const res = await API.getAction(`organization/AdminOrganizationStaff?organizationId=${orgId}`)
      if (res.data.status === "ok") setStaff(res.data.response)
    } catch { toast.error("Error cargando usuarios") }
    finally { setStaffLoading(false) }
  }

  const fetchRoles = async (orgId: string) => {
    try {
      const res = await API.getAction(`organization/Roles?organizationID=${orgId}`)
      if (res.data.status === "ok") setRoles(res.data.response)
    } catch {}
  }

  const searchUsers = async (value: string) => {
    setUserSearch(value)
    setSelectedUserId("")
    setSelectedUserLabel("")
    if (value.length < 2) { setUserResults([]); return }
    setUserSearchLoading(true)
    try {
      const res = await API.getAction(`users/AdminUsers?search=${encodeURIComponent(value)}&quantity=10&page=0`)
      if (res.data.status === "ok") setUserResults(res.data.response)
    } catch {} finally { setUserSearchLoading(false) }
  }

  const selectUser = (u: UserSearchResult) => {
    setSelectedUserId(u.id)
    setSelectedUserLabel(`${u.firstName} ${u.lastName} (${u.email})`)
    setUserSearch(`${u.firstName} ${u.lastName} (${u.email})`)
    setUserResults([])
  }

  const assignUser = async () => {
    if (!selectedUserId || !selectedOrg) return
    setAssigning(true)
    try {
      const res = await API.postAction("organization/AdminAssignUserToOrg", {
        organizationId: selectedOrg.id,
        userId: selectedUserId,
        roleId: selectedRoleId || null,
      })
      if (res.data.status === "ok") {
        toast.success("Usuario asignado exitosamente")
        setSelectedUserId(""); setSelectedUserLabel(""); setSelectedRoleId("")
        setUserSearch(""); setUserResults([])
        await fetchStaff(selectedOrg.id)
      }
    } catch (err: any) {
      if (err?.response?.data?.response === "ALREADY_MEMBER") {
        toast.warning("Este usuario ya pertenece a la compañía")
      } else {
        toast.error("Error al asignar usuario")
      }
    } finally { setAssigning(false) }
  }

  const removeUser = (s: StaffRecord) => {
    if (!selectedOrg) return
    confirmAlert({
      closeOnClickOutside: false,
      message: `¿Remover a ${s.fullName} de "${selectedOrg.name}"?`,
      buttons: [
        {
          label: "Sí, remover", onClick: async () => {
            try {
              await API.getAction(`organization/LeaveOrganization?organizationId=${selectedOrg.id}&userId=${s.userId}`)
              toast.success("Usuario removido")
              await fetchStaff(selectedOrg.id)
            } catch { toast.error("Error al remover usuario") }
          }
        },
        { label: "Cancelar", onClick: () => { return } },
      ],
    })
  }

  // ── Activity ──────────────────────────────────────────────────────────────────
  const fetchActivity = async (orgId: string, p: number) => {
    setActivityLoading(true)
    try {
      const res = await API.getAction(
        `activitylog/List?organizationId=${orgId}&page=${p}&quantity=${LOG_SIZE}`
      )
      if (res.data.status === "ok") {
        setActivity(res.data.response)
        setActivityTotalPages(res.data.quantityOfPages || 1)
      }
    } catch { toast.error("Error cargando actividad") }
    finally { setActivityLoading(false) }
  }

  const changeActivityPage = async (p: number) => {
    if (!selectedOrg) return
    setActivityPage(p)
    await fetchActivity(selectedOrg.id, p)
  }

  // ── Add/Edit ──────────────────────────────────────────────────────────────────
  const openAddEdit = (org?: OrgRecord) => {
    setEditOrg(org ? { id: org.id, name: org.name, identifier: org.identifier } : {})
    setShowAddEdit(true)
  }

  const saveOrg = async () => {
    if (!editOrg.name?.trim()) { toast.warning("El nombre es requerido"); return }
    setSaving(true)
    try {
      await API.postAction("organization/AddEdit", editOrg)
      toast.success("Compañía guardada")
      setShowAddEdit(false)
      fetchRecords(page, search)
    } catch { toast.error("Error al guardar") }
    finally { setSaving(false) }
  }

  const deleteOrg = (org: OrgRecord) => {
    confirmAlert({
      closeOnClickOutside: false,
      message: `¿Eliminar la compañía "${org.name}"?`,
      buttons: [
        {
          label: "Sí, eliminar", onClick: async () => {
            try {
              await API.getAction(`organization/Delete?id=${org.id}`)
              toast.success("Compañía eliminada")
              fetchRecords(page, search)
            } catch { toast.error("Error al eliminar") }
          }
        },
        { label: "Cancelar", onClick: () => { return } },
      ],
    })
  }

  const actionBadge = (action: string) => {
    const map: Record<string, string> = { Create: "success", Edit: "primary", Delete: "danger", Search: "secondary" }
    return <Badge bg={map[action] ?? "light"} text={map[action] ? "white" : "dark"}>{action}</Badge>
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3 mt-2">
        <h5 className="mb-0">
          <i className="fas fa-sitemap me-2" />
          {t("Organizations")}
        </h5>
        <button className="btn btn-primary btn-sm" onClick={() => openAddEdit()}>
          <i className="fa fa-plus me-1" /> Nueva Compañía
        </button>
      </div>

      {/* Search */}
      <form className="mb-3 d-flex gap-2" onSubmit={handleSearch}>
        <input
          className="form-control form-control-sm"
          placeholder="Buscar por nombre o identificador..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button className="btn btn-secondary btn-sm" type="submit">
          <i className="fa fa-search" />
        </button>
      </form>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-striped table-bordered table-sm">
          <thead className="table-dark">
            <tr>
              <th>{t("Name")}</th>
              <th>RNC / Identificador</th>
              <th>Creador</th>
              <th>Registrado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={5} className="text-center py-3">
                <div className="spinner-border spinner-border-sm" role="status" />
              </td></tr>
            )}
            {!isLoading && records.length === 0 && (
              <tr><td colSpan={5} className="text-center py-3 text-muted">{t("no_records")}</td></tr>
            )}
            {!isLoading && records.map(org => (
              <tr key={org.id}>
                <td className="fw-semibold">{org.name}</td>
                <td><code>{org.identifier || "-"}</code></td>
                <td>
                  <div className="small">{org.creatorName}</div>
                  <div className="small text-muted">{org.creatorEmail}</div>
                </td>
                <td className="small">{org.created ? moment(org.created).format("MMM DD, YYYY") : "-"}</td>
                <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                  <div className="btn-group btn-group-sm">
                    <button className="btn btn-info text-white" title="Ver perfil" onClick={() => openProfile(org)}>
                      <i className="fa fa-building me-1" /> Perfil
                    </button>
                    <button className="btn btn-outline-secondary" title="Editar" onClick={() => openAddEdit(org)}>
                      <i className="fa fa-edit" />
                    </button>
                    <button className="btn btn-outline-danger" title="Eliminar" onClick={() => deleteOrg(org)}>
                      <i className="fa fa-trash" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="d-flex gap-1 flex-wrap mb-4">
        <button className="btn btn-sm btn-outline-secondary" disabled={page === 0} onClick={() => setPage(0)}>«</button>
        <button className="btn btn-sm btn-outline-secondary" disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹</button>
        {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
          const start = Math.max(0, page - 3)
          const p = start + i
          if (p >= totalPages) return null
          return (
            <button key={p} className={`btn btn-sm ${p === page ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setPage(p)}>
              {p + 1}
            </button>
          )
        })}
        <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>›</button>
        <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>»</button>
        <span className="align-self-center small text-muted ms-2">Pág. {page + 1} / {totalPages}</span>
      </div>

      {/* ── Add/Edit Modal ────────────────────────────────────────────── */}
      <Modal show={showAddEdit} onHide={() => setShowAddEdit(false)} size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">{editOrg.id ? "Editar Compañía" : "Nueva Compañía"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label form-label-sm">Nombre *</label>
            <input className="form-control form-control-sm" value={editOrg.name || ""}
              onChange={e => setEditOrg(v => ({ ...v, name: e.target.value }))} />
          </div>
          <div className="mb-3">
            <label className="form-label form-label-sm">RNC / Identificador</label>
            <input className="form-control form-control-sm" value={editOrg.identifier || ""}
              onChange={e => setEditOrg(v => ({ ...v, identifier: e.target.value }))} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowAddEdit(false)}>{t("Cancel")}</Button>
          <Button variant="primary" size="sm" disabled={saving} onClick={saveOrg}>
            {saving && <span className="spinner-border spinner-border-sm me-1" />}
            {t("Save")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Company Profile Modal ─────────────────────────────────────── */}
      <Modal show={showProfile} onHide={() => setShowProfile(false)} size="xl" scrollable>
        <Modal.Header closeButton className="pb-0">
          <div className="w-100">
            {/* Profile header */}
            <div className="d-flex align-items-center gap-3 mb-2">
              <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: 52, height: 52, fontSize: 22, flexShrink: 0 }}>
                <i className="fas fa-building" />
              </div>
              <div>
                <h5 className="mb-0">{selectedOrg?.name}</h5>
                {selectedOrg?.identifier && (
                  <span className="text-muted small">RNC / Identificador: <code>{selectedOrg.identifier}</code></span>
                )}
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-0">
          <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
            <Nav variant="tabs" className="px-3 pt-2 border-bottom bg-light">
              <Nav.Item>
                <Nav.Link eventKey="info"><i className="fa fa-info-circle me-1" />Información</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="users">
                  <i className="fa fa-users me-1" />Usuarios
                  {staff.length > 0 && <Badge bg="secondary" className="ms-1">{staff.length}</Badge>}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="activity"><i className="fa fa-history me-1" />Actividad</Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content className="p-3">
              {/* ── INFO TAB ───────────────────────────────────────────── */}
              <Tab.Pane eventKey="info">
                {selectedOrg && (
                  <div className="row g-3">
                    {/* Basic Info Card */}
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-header fw-semibold small text-muted">
                          <i className="fa fa-id-card me-1" />DATOS GENERALES
                        </div>
                        <div className="card-body">
                          <table className="table table-sm table-borderless mb-0">
                            <tbody>
                              <tr>
                                <td className="text-muted small" style={{ width: 140 }}>Nombre</td>
                                <td className="fw-semibold">{selectedOrg.name}</td>
                              </tr>
                              <tr>
                                <td className="text-muted small">Identificador</td>
                                <td><code>{selectedOrg.identifier || "-"}</code></td>
                              </tr>
                              <tr>
                                <td className="text-muted small">Fecha registro</td>
                                <td>{selectedOrg.created ? moment(selectedOrg.created).format("DD/MM/YYYY HH:mm") : "-"}</td>
                              </tr>
                              {selectedOrg.subscriptionName && (
                                <tr>
                                  <td className="text-muted small">Suscripción</td>
                                  <td><Badge bg="info">{selectedOrg.subscriptionName}</Badge></td>
                                </tr>
                              )}
                              {selectedOrg.nextBillingDate && (
                                <tr>
                                  <td className="text-muted small">Próx. factura</td>
                                  <td>{moment(selectedOrg.nextBillingDate).format("DD/MM/YYYY")}</td>
                                </tr>
                              )}
                              {selectedOrg.modules && (
                                <tr>
                                  <td className="text-muted small">Módulos</td>
                                  <td>{selectedOrg.modules}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Creator Card */}
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-header fw-semibold small text-muted">
                          <i className="fa fa-user me-1" />CREADOR / ADMINISTRADOR
                        </div>
                        <div className="card-body">
                          <div className="d-flex align-items-center gap-3 mb-2">
                            <div className="bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: 40, height: 40, flexShrink: 0 }}>
                              <i className="fa fa-user" />
                            </div>
                            <div>
                              <div className="fw-semibold">{selectedOrg.creatorName}</div>
                              <div className="small text-muted">{selectedOrg.creatorEmail}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Stats Card */}
                    <div className="col-12">
                      <div className="card">
                        <div className="card-header fw-semibold small text-muted">
                          <i className="fa fa-chart-bar me-1" />ESTADÍSTICAS
                        </div>
                        <div className="card-body">
                          <div className="row text-center">
                            <div className="col-4">
                              <div className="fs-4 fw-bold text-primary">{staff.length}</div>
                              <div className="small text-muted">Usuarios asignados</div>
                            </div>
                            <div className="col-4">
                              <div className="fs-4 fw-bold text-success">{roles.length}</div>
                              <div className="small text-muted">Roles configurados</div>
                            </div>
                            <div className="col-4">
                              <div className="fs-4 fw-bold text-info">
                                {selectedOrg.created ? moment().diff(moment(selectedOrg.created), "days") : "-"}
                              </div>
                              <div className="small text-muted">Días activa</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Roles Card */}
                    {roles.length > 0 && (
                      <div className="col-12">
                        <div className="card">
                          <div className="card-header fw-semibold small text-muted">
                            <i className="fa fa-shield-alt me-1" />ROLES CONFIGURADOS
                          </div>
                          <div className="card-body pb-1">
                            <div className="d-flex flex-wrap gap-2">
                              {roles.map(r => (
                                <Badge key={r.id} bg="secondary" className="fs-6 fw-normal py-1 px-2">{r.name}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Tab.Pane>

              {/* ── USERS TAB ──────────────────────────────────────────── */}
              <Tab.Pane eventKey="users">
                {/* Assign User */}
                <div className="card mb-3">
                  <div className="card-header fw-semibold small text-muted">
                    <i className="fa fa-user-plus me-1" />ASIGNAR USUARIO
                  </div>
                  <div className="card-body">
                    <div className="row g-2 align-items-end">
                      <div className="col-md-5">
                        <label className="form-label small mb-1">Buscar usuario (nombre o email)</label>
                        <div className="position-relative">
                          <input
                            className="form-control form-control-sm"
                            placeholder="Escribe al menos 2 caracteres..."
                            value={userSearch}
                            onChange={e => searchUsers(e.target.value)}
                          />
                          {userSearchLoading && (
                            <div className="position-absolute end-0 top-50 translate-middle-y me-2">
                              <div className="spinner-border spinner-border-sm text-secondary" />
                            </div>
                          )}
                        </div>
                        {userResults.length > 0 && !selectedUserId && (
                          <div className="border rounded shadow-sm mt-1 bg-white" style={{ maxHeight: 180, overflowY: "auto", zIndex: 10, position: "relative" }}>
                            {userResults.map(u => (
                              <div key={u.id} className="px-3 py-2 border-bottom"
                                style={{ cursor: "pointer" }}
                                onMouseEnter={e => (e.currentTarget.style.background = "#f0f0f0")}
                                onMouseLeave={e => (e.currentTarget.style.background = "")}
                                onClick={() => selectUser(u)}>
                                <div className="small fw-semibold">{u.firstName} {u.lastName}</div>
                                <div className="small text-muted">{u.email}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {selectedUserId && (
                          <div className="mt-1 small text-success">
                            <i className="fa fa-check-circle me-1" />{selectedUserLabel}
                            <button className="btn btn-link btn-sm p-0 ms-2 text-danger" onClick={() => { setSelectedUserId(""); setSelectedUserLabel(""); setUserSearch("") }}>
                              <i className="fa fa-times" />
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small mb-1">Rol (opcional)</label>
                        <select className="form-select form-select-sm" value={selectedRoleId} onChange={e => setSelectedRoleId(e.target.value)}>
                          <option value="">Sin rol específico</option>
                          {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <button className="btn btn-primary btn-sm w-100" disabled={!selectedUserId || assigning} onClick={assignUser}>
                          {assigning ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="fa fa-plus me-1" />}
                          {t("Assign")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Staff Table */}
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <span className="fw-semibold small text-muted">
                      <i className="fa fa-users me-1" />USUARIOS ASIGNADOS
                    </span>
                    <Badge bg="secondary">{staff.length}</Badge>
                  </div>
                  <div className="card-body p-0">
                    {staffLoading ? (
                      <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm table-striped mb-0">
                          <thead>
                            <tr>
                              <th>Nombre</th>
                              <th>Email</th>
                              <th>Rol</th>
                              <th>Asignado</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {staff.length === 0 && (
                              <tr><td colSpan={5} className="text-center py-3 text-muted">Sin usuarios asignados</td></tr>
                            )}
                            {staff.map(s => (
                              <tr key={s.staffId}>
                                <td className="fw-semibold">{s.fullName}</td>
                                <td className="small">{s.email}</td>
                                <td>{s.roleName ? <Badge bg="info">{s.roleName}</Badge> : <span className="text-muted small">-</span>}</td>
                                <td className="small">{s.joined ? moment(s.joined).format("DD/MM/YYYY") : "-"}</td>
                                <td className="text-end">
                                  <button className="btn btn-sm btn-outline-danger" title="Remover usuario" onClick={() => removeUser(s)}>
                                    <i className="fa fa-user-minus" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </Tab.Pane>

              {/* ── ACTIVITY TAB ───────────────────────────────────────── */}
              <Tab.Pane eventKey="activity">
                {activityLoading ? (
                  <div className="text-center py-5"><div className="spinner-border" /></div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-sm table-striped table-bordered">
                        <thead className="table-dark">
                          <tr>
                            <th style={{ width: 140 }}>Fecha</th>
                            <th>Usuario</th>
                            <th>Sección</th>
                            <th>Acción</th>
                            <th>Descripción</th>
                            <th>IP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activity.length === 0 && (
                            <tr><td colSpan={6} className="text-center py-3 text-muted">Sin actividad registrada</td></tr>
                          )}
                          {activity.map((a, i) => (
                            <tr key={i}>
                              <td className="small text-nowrap">{a.date ? moment(a.date).format("DD/MM/YY HH:mm") : "-"}</td>
                              <td className="small">{a.userName || "-"}</td>
                              <td><Badge bg="light" text="dark" className="border">{a.section || "-"}</Badge></td>
                              <td>{a.action ? actionBadge(a.action) : "-"}</td>
                              <td className="small" dangerouslySetInnerHTML={{ __html: a.content || "-" }} />
                              <td className="small text-muted">{a.ipAddress || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Activity Pagination */}
                    <div className="d-flex gap-1 mt-2">
                      <button className="btn btn-sm btn-outline-secondary" disabled={activityPage === 0} onClick={() => changeActivityPage(activityPage - 1)}>‹ Anterior</button>
                      <span className="align-self-center small text-muted mx-2">Pág. {activityPage + 1} / {activityTotalPages}</span>
                      <button className="btn btn-sm btn-outline-secondary" disabled={activityPage >= activityTotalPages - 1} onClick={() => changeActivityPage(activityPage + 1)}>Siguiente ›</button>
                    </div>
                  </>
                )}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Modal.Body>

        <Modal.Footer className="py-2">
          <Button variant="outline-secondary" size="sm" onClick={() => openAddEdit(selectedOrg!)}>
            <i className="fa fa-edit me-1" />Editar compañía
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setShowProfile(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default AdminOrganizationsComponent
